import {Button, FormControlLabel, Paper, Switch} from '@material-ui/core';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import qs from 'query-string';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useLocation} from 'react-router-dom';
import {bindActionCreators} from 'redux';
import {ReducerState} from 'rootReducer';
import Alert from 'Shared/components/Alert';
import {NoErrors} from 'Shared/components/ErrorPopover';
import Loading from 'Shared/components/Loading';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import withUserClaims from 'Shared/components/withUserClaims';
import {ActionsOf} from 'Shared/utilities/actionCreator';
import 'Users/components/SingleSignOn/SingleSignOnAccount.sass';
import * as SSOAccount from 'Users/ducks/SSO/ssoAccount';
import {UserClaims} from 'Users/models/Claims';
import { fromAsync } from 'Core/models/Async';
import {
  InvalidPassword,
  InvalidUsername,
  matchSSOAccount,
  SSOAccountWithError,
  ValidSSOAccount,
} from 'Users/models/SSOAccount';
import {UserRouteConstants} from 'Users/Routes';
import {changeAnalyticsState} from 'Core/ducks/analytics';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';

type SingleSignOnAccountProps = {
  claims: UserClaims;
} & WithSelectedAgencyProps;

const SingleSignOnAccount = (props: SingleSignOnAccountProps) => {
  const dispatch = useDispatch();
  const {account, accounts} = useSelector((state: ReducerState) => state.user.sso);
  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);
  const accountsData = fromAsync(accounts);  
  const hasPrimaryAccount = Array.isArray(accountsData) && accountsData.some(acc => acc.isPrimary);

  const {selectedAgency} = props;
  const {search, pathname} = useLocation();
  const username = qs.parse(search).username as string;
  const {analytics} = useSelector((state: ReducerState) => state.analytics);

  useBreadcrumbs('Single Sign-On Accounts', UserRouteConstants.SSO_ACCOUNTS);
  useBreadcrumbs(username || 'Add New SSO Account', `${pathname}${search}`);
  const isEdit = !!username;
  const {getAccount, changeUsername, changePassword, saveAccount, changeIsPrimary} = bindActionCreators(
    SSOAccount as ActionsOf<typeof SSOAccount>,
    dispatch
  );

  useEffect(() => {
    if (username) {
      getAccount(selectedAgency.encryptedIdentifier, props.claims.id, username);
    }

    return () => {
      dispatch(SSOAccount.clearAccount());
    };
  }, [username]);

  useEffect(() => {
    dispatch(updatefeatureVariables({...featureVariables, FOOTER_OPEN_DEFAULT: false}));
  }, []);

  const SSOForm = (
    <form className="sso-account__form">
      <h1 className="sso-account__header">{isEdit ? 'Edit' : 'Add'} Account</h1>
      <p className="sso-account__description">Please enter your legacy account credentials below.</p>
      {account instanceof SSOAccountWithError && (
        <Alert message={account.errorMessage || 'An Error Ocurred While Saving Your Account. Please Try Again.'} />
      )}
      <div className="sso-account__fields">
        <ValidatableTextField
          disabled={isEdit}
          label="Username"
          className="sso-account__field"
          onChange={changeUsername}
          errors={account.username instanceof InvalidUsername ? account.username.error : NoErrors}
          value={account.username.value.value}
        />
        <ValidatableTextField
          label="Password"
          className="sso-account__field"
          inputProps={{
            type: 'password',
          }}
          onChange={changePassword}
          errors={account.password instanceof InvalidPassword ? account.password.error : NoErrors}
          value={account.password.value.value}
        />
        <div>
          {!isEdit && (
            <FormControlLabel
              className="sso-account__field"
              labelPlacement="start"
              control={
                <Switch
                  color="primary"
                  checked={account.isPrimary}
                  onChange={e => changeIsPrimary(e.currentTarget.checked)}
                  disabled={hasPrimaryAccount}
                />
              }
              label="Is this a New Account?"
            />
          )}
        </div>
      </div>
      <div className="sso-account__actions">
        <Button
          className="sso-account__submit-button"
          disabled={!(account instanceof ValidSSOAccount)}
          onClick={() => {
            if (account instanceof ValidSSOAccount) {
              saveAccount(selectedAgency.encryptedIdentifier, props.claims.id, account, !!username);
              dispatch(
                changeAnalyticsState({
                  page: analytics.page,
                  ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.SINGLE_SIGNON_HEADER_ADD_ACCOUNT_SUBMIT},
                })
              );
            }
          }}
          color="primary"
          variant="contained"
          type="submit"
        >
          Submit
        </Button>
        <Link to={UserRouteConstants.SSO_ACCOUNTS}>
          <Button
            onClick={() => {
              dispatch(
                changeAnalyticsState({
                  page: analytics.page,
                  ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.SINGLE_SIGNON_HEADER_ADD_ACCOUNT_CANCEL},
                })
              );
            }}
          >
            Cancel
          </Button>
        </Link>
      </div>
    </form>
  );

  return (
    <div className="sso-account">
      <Paper className="sso-account__paper">
        {matchSSOAccount(account, {
          SSOAccountWithError: () => SSOForm,
          InvalidSSOAccount: () => SSOForm,
          ValidSSOAccount: () => SSOForm,
          LoadingSSOAccount: Loading,
          SavingSSOAccount: Loading,
        })}
      </Paper>
    </div>
  );
};

export default withSelectedAgency(withUserClaims(SingleSignOnAccount));
